<template>
  <Teleport to="body">
  <div v-if="visible" ref="popoverRef" class="fixed w-26rem fadein animation-duration-200"
    :style="[popoverStyle, { zIndex: 10000, maxWidth: '95vw' }]" @mouseleave="handlePopoverMouseLeave"
    @mouseenter="handlePopoverMouseEnter">
    <div class="surface-card border-round-xl shadow-6 p-4 border-1 surface-border">
      <div class="flex justify-content-between align-items-start mb-4">
        <h2 class="m-0 text-2xl font-bold text-900" style="overflow-wrap: break-word; word-break: break-word; min-width: 0;">{{ cleanupLables(event?.title) }}</h2>
        <button
          class="bg-transparent border-none cursor-pointer text-500 hover:text-700 transition-colors transition-duration-200 p-0 flex align-items-center justify-content-center"
          @click="handleClose">
          <i class="pi pi-times text-xl"></i>
        </button>
      </div>

      <div class="flex flex-column gap-3">
        <div class="flex flex-column gap-2">
          <div class="flex align-items-start">
            <span class="text-500 text-sm w-6rem flex-shrink-0">Tutor -</span>
            <span class="text-800 text-sm font-medium">{{ event?.extendedProps?.tutor || 'N/A' }}</span>
          </div>
          <div class="flex align-items-start">
            <span class="text-500 text-sm w-6rem flex-shrink-0">Student -</span>
            <span class="text-800 text-sm font-medium">{{ event?.extendedProps?.with || 'N/A' }}</span>
          </div>
        </div>

        <div class="flex flex-column">
          <div class="text-800 text-sm font-medium capitalize mb-1">
            {{ event?.extendedProps?.classType }}
          </div>
          <div class="text-800 text-sm">
            {{ formatEventTime(event) }}
          </div>
        </div>
        
        <div v-if="event?.extendedProps?.classLink" class="my-2 flex align-items-center gap-2">
          <a :href="event.extendedProps.classLink" target="_blank"
            class="text-blue-500 no-underline text-lg font-semibold">
            Google Meet Link
          </a>
          <button
            class="bg-transparent border-none cursor-pointer p-1 flex align-items-center justify-content-center"
            :title="linkCopied ? 'Copied!' : 'Copy link'"
            @click.prevent="copyLink(event.extendedProps.classLink)">
            <i :class="linkCopied ? 'pi pi-check text-green-500' : 'pi pi-copy text-blue-500'" class="text-base"></i>
          </button>
        </div>

        <div class="flex flex-column gap-2">
          <div class="text-800 font-semibold text-base">{{ cleanupLables(event?.extendedProps?.classModel) }}</div>
          <div v-if="event?.extendedProps?.otherStudents" class="flex align-items-start">
            <span class="text-500 text-sm w-8rem flex-shrink-0">Other Students -</span>
            <div class="text-800 text-sm flex flex-column">
              <div v-for="(student, idx) in event.extendedProps.otherStudents" :key="idx">
                {{ student.replace(/,/g, "") }}
              </div>
            </div>
          </div>
        </div>

        <div v-if="event?.extendedProps?.note" class="mt-3">
          <div class="flex align-items-start">
            <span class="text-500 text-sm w-6rem flex-shrink-0">Note -</span>
            <span class="text-800 text-sm line-height-3">
              {{ event?.extendedProps?.note }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<script>
import { ref, watch, onMounted, onUnmounted, defineComponent } from 'vue';
import { cleanupLables } from "@/utils/cleanups";

// Composable function - exported separately
export function useEventPopover() {
  const showEventPopover = ref(false);
  const selectedEvent = ref(null);
  const isEventLocked = ref(false);
  const eventElement = ref(null);
  const hoverTimeout = ref(null);

  // Handle event click - locks the popover
  const handleEventClick = (info) => {
    info.jsEvent.preventDefault();
    selectedEvent.value = info.event;
    isEventLocked.value = true;
    showEventPopover.value = true;
    eventElement.value = info.jsEvent.target.closest('.fc-event');
  };

  // Handle event mouse enter - shows popover on hover (if not locked)
  const handleEventMouseEnter = (info) => {
    if (isEventLocked.value) return;

    if (hoverTimeout.value) {
      clearTimeout(hoverTimeout.value);
    }

    hoverTimeout.value = setTimeout(() => {
      selectedEvent.value = info.event;
      showEventPopover.value = true;
      eventElement.value = info.jsEvent.target.closest('.fc-event');
    }, 300);
  };

  // Handle event mouse leave - hides popover (if not locked)
  const handleEventMouseLeave = () => {
    if (isEventLocked.value) return;

    if (hoverTimeout.value) {
      clearTimeout(hoverTimeout.value);
    }

    hoverTimeout.value = setTimeout(() => {
      if (!isEventLocked.value) {
        showEventPopover.value = false;
        selectedEvent.value = null;
        eventElement.value = null;
      }
    }, 200);
  };

  // Close the event popover
  const closeEventPopover = () => {
    showEventPopover.value = false;
    selectedEvent.value = null;
    isEventLocked.value = false;
    eventElement.value = null;
  };

  // Clean up timeouts
  const cleanup = () => {
    if (hoverTimeout.value) {
      clearTimeout(hoverTimeout.value);
    }
  };

  return {
    showEventPopover,
    selectedEvent,
    isEventLocked,
    eventElement,
    handleEventClick,
    handleEventMouseEnter,
    handleEventMouseLeave,
    closeEventPopover,
    cleanup
  };
}

// Component definition - default export
export default defineComponent({
  name: 'EventPopover',
  props: {
    visible: {
      type: Boolean,
      required: true
    },
    event: {
      type: Object,
      default: null
    },
    isLocked: {
      type: Boolean,
      default: false
    },
    eventElement: {
      type: Object,
      default: null
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const popoverRef = ref(null);
    const popoverStyle = ref({});
    const hoverTimeout = ref(null);
    const linkCopied = ref(false);

    // eslint-disable-next-line no-unused-vars
    const copyLink = (url) => {
      navigator.clipboard.writeText(url).then(() => {
        linkCopied.value = true;
        setTimeout(() => { linkCopied.value = false; }, 2000);
      });
    };

    // Format event time for display in popover
    const formatEventTime = (event) => {
      if (!event) return 'N/A';

      // Use original IST display times if available (avoids showing shifted dates)
      const startDate = event.extendedProps?.displayStart
        ? new Date(event.extendedProps.displayStart)
        : event.start;
      const endDate = event.extendedProps?.displayEnd
        ? new Date(event.extendedProps.displayEnd)
        : event.end;

      if (!startDate || !endDate) return 'N/A';

      const startTime = startDate.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      const endTime = endDate.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      const date = startDate.toLocaleDateString([], {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });

      return `${date}, ${startTime} - ${endTime}`;
    };

    // Position the popover near the event
    const positionPopover = () => {
      if (!props.eventElement) return;

      const rect = props.eventElement.getBoundingClientRect();
      const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      const isMobile = viewportWidth <= 640;

      if (isMobile) {
        // On mobile: center horizontally, show above or below the event
        const popoverWidth = Math.min(viewportWidth - 20, 416);
        const left = (viewportWidth - popoverWidth) / 2;

        let top = rect.bottom + 8;
        if (top + 420 > viewportHeight) {
          top = rect.top - 428;
        }
        if (top < 10) {
          top = 10;
        }

        popoverStyle.value = {
          left: `${left}px`,
          top: `${top}px`,
          width: `${popoverWidth}px`
        };
      } else {
        // On desktop: prefer right side, fall back to left
        let left = rect.right + 10;
        let top = rect.top;

        if (left + 416 > viewportWidth) {
          left = rect.left - 426;
        }
        if (left < 10) {
          left = 10;
        }
        if (top + 400 > viewportHeight) {
          top = viewportHeight - 420;
        }
        if (top < 10) {
          top = 10;
        }

        popoverStyle.value = {
          left: `${left}px`,
          top: `${top}px`,
          width: ''
        };
      }
    };

    // Reposition when scrolling or resizing
    const handleScroll = () => {
      if (props.visible && props.eventElement) {
        positionPopover();
      }
    };

    const handleResize = () => {
      if (props.visible && props.eventElement) {
        positionPopover();
      }
    };

    // Handle popover mouse enter - keep it visible
    const handlePopoverMouseEnter = () => {
      if (hoverTimeout.value) {
        clearTimeout(hoverTimeout.value);
      }
    };

    // Handle popover mouse leave - hide if not locked
    const handlePopoverMouseLeave = () => {
      if (props.isLocked) return;

      hoverTimeout.value = setTimeout(() => {
        emit('close');
      }, 200);
    };

    // Close the event popover
    const handleClose = () => {
      emit('close');
    };

    // Watch for visibility and eventElement changes to reposition
    watch(() => [props.visible, props.eventElement], () => {
      if (props.visible && props.eventElement) {
        positionPopover();
      }
    }, { immediate: true });

    onMounted(() => {
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
    });

    onUnmounted(() => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);

      // Clear any pending timeouts
      if (hoverTimeout.value) {
        clearTimeout(hoverTimeout.value);
      }
    });

    return {
      popoverRef,
      popoverStyle,
      formatEventTime,
      handlePopoverMouseEnter,
      handlePopoverMouseLeave,
      handleClose,
      cleanupLables,
      linkCopied,
      copyLink
    };
  }
});
</script>

<style scoped></style>